import { normalizeCatalogCustomFields } from '../pluginFields'
import type {
  CatalogMetadataEntry,
  CatalogProduct,
  CatalogProductDocument,
  CatalogProductVariant,
  CatalogProductPurchasability,
  CatalogProductPurchasabilityReason,
  CatalogProductStockPolicy,
} from '../types'
import { toNumber } from './shared'

const PUBLIC_CODE_PATTERN = /^[A-HJ-NP-Z2-9]{8}$/

const normalizeStockPolicy = (value: unknown): CatalogProductStockPolicy | undefined => {
  if (value === 'allow-backorder') return 'allow-backorder'
  if (value === 'deny-oversell') return 'deny-oversell'
  return undefined
}

const normalizePublicCode = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const normalized = value.trim().toUpperCase()
  if (!PUBLIC_CODE_PATTERN.test(normalized)) return undefined
  return normalized
}

const normalizePurchasabilityReason = (value: unknown): CatalogProductPurchasabilityReason | null => {
  if (value === 'status_not_active') return 'status_not_active'
  if (value === 'missing_required_fields') return 'missing_required_fields'
  if (value === 'invalid_price') return 'invalid_price'
  if (value === 'out_of_stock') return 'out_of_stock'
  return null
}

const normalizeProductPurchasability = (raw: unknown): CatalogProductPurchasability | undefined => {
  if (!raw || typeof raw !== 'object') return undefined
  const data = raw as Record<string, unknown>
  const isPurchasable = typeof data.isPurchasable === 'boolean' ? data.isPurchasable : undefined
  if (isPurchasable === undefined) return undefined
  const stockPolicy = normalizeStockPolicy(data.stockPolicy) ?? 'deny-oversell'
  const availableStock = toNumber(data.availableStock)
  return {
    isPurchasable,
    reason: normalizePurchasabilityReason(data.reason),
    stockPolicy,
    availableStock: Math.max(0, Math.trunc(availableStock ?? 0)),
    hasValidPrice: typeof data.hasValidPrice === 'boolean' ? data.hasValidPrice : undefined,
    status:
      data.status === 'active' || data.status === 'draft' || data.status === 'archived'
        ? data.status
        : undefined,
  }
}

const normalizeVariantFields = (value: unknown): CatalogProductVariant['attributes'] | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const fields = Object.entries(value as Record<string, unknown>).reduce<NonNullable<CatalogProductVariant['attributes']>>(
    (next, [key, fieldValue]) => {
      const normalizedKey = key.trim()
      if (!normalizedKey) return next
      if (fieldValue === null) {
        next[normalizedKey] = null
      } else if (typeof fieldValue === 'string' || typeof fieldValue === 'boolean') {
        next[normalizedKey] = fieldValue
      } else if (typeof fieldValue === 'number' && Number.isFinite(fieldValue)) {
        next[normalizedKey] = fieldValue
      } else if (Array.isArray(fieldValue)) {
        next[normalizedKey] = fieldValue
          .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
          .filter(Boolean)
      }
      return next
    },
    {},
  )
  return Object.keys(fields).length ? fields : undefined
}

const normalizeProductVariant = (raw: unknown): CatalogProductVariant | null => {
  if (!raw || typeof raw !== 'object') return null
  const data = raw as Record<string, unknown>
  const id = typeof data.id === 'string' && data.id.trim() ? data.id.trim() : undefined
  const sku = typeof data.sku === 'string' && data.sku.trim() ? data.sku.trim() : undefined
  const name = typeof data.name === 'string' && data.name.trim() ? data.name.trim() : sku ?? id ?? ''
  const attributes = normalizeVariantFields(data.attributes)
  const overrides = normalizeVariantFields(data.overrides)
  if (!name && !attributes && !overrides) return null

  return {
    id,
    sku,
    status:
      data.status === 'active' || data.status === 'draft' || data.status === 'archived'
        ? data.status
        : undefined,
    name: name || 'Variante',
    description: typeof data.description === 'string' ? data.description : undefined,
    attributes,
    price: toNumber(data.price),
    // Read legacy `stock` values once, then expose only canonical `quantity`.
    quantity: toNumber(data.quantity) ?? toNumber(data.stock),
    overrides,
    mediaVariants:
      data.mediaVariants && typeof data.mediaVariants === 'object'
        ? (data.mediaVariants as CatalogProductVariant['mediaVariants'])
        : null,
  }
}

const normalizeProductVariants = (raw: unknown): CatalogProductVariant[] | undefined => {
  if (!Array.isArray(raw)) return undefined
  const variants = raw.map(normalizeProductVariant).filter((variant): variant is CatalogProductVariant => Boolean(variant))
  return variants.length ? variants : undefined
}

const filenameFromUrl = (url: string) => {
  try {
    const parsed = new URL(url)
    return decodeURIComponent(parsed.pathname.split('/').filter(Boolean).pop() ?? url)
  } catch {
    return decodeURIComponent(url.split('?')[0]?.split('/').filter(Boolean).pop() ?? url)
  }
}

const normalizeProductDocument = (raw: unknown, index: number): CatalogProductDocument | null => {
  if (typeof raw === 'string') {
    const url = raw.trim()
    if (!url) return null
    const name = filenameFromUrl(url)
    return { id: `${url}-${index}`, name, originalName: name, url, mimeType: '', size: 0 }
  }

  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const data = raw as Record<string, unknown>
  const url = typeof data.url === 'string' ? data.url.trim() : ''
  if (!url) return null
  const fallbackName = filenameFromUrl(url)
  const originalName =
    typeof data.originalName === 'string' && data.originalName.trim()
      ? data.originalName.trim()
      : typeof data.name === 'string' && data.name.trim()
      ? data.name.trim()
      : fallbackName
  const name = typeof data.name === 'string' && data.name.trim() ? data.name.trim() : originalName
  return {
    id: typeof data.id === 'string' && data.id.trim() ? data.id.trim() : `${url}-${index}`,
    name,
    originalName,
    url,
    mimeType: typeof data.mimeType === 'string' ? data.mimeType.trim() : '',
    size: typeof data.size === 'number' && Number.isFinite(data.size) && data.size > 0 ? data.size : 0,
  }
}

const normalizeProductDocuments = (raw: unknown): CatalogProductDocument[] => {
  if (!Array.isArray(raw)) return []
  const seen = new Set<string>()
  return raw.reduce<CatalogProductDocument[]>((documents, item, index) => {
    const document = normalizeProductDocument(item, index)
    if (!document || seen.has(document.url)) return documents
    seen.add(document.url)
    documents.push(document)
    return documents
  }, [])
}

export const normalizeProduct = (raw: Record<string, unknown>): CatalogProduct => {
  const metadata =
    Array.isArray(raw.metadata)
      ? raw.metadata
          .map((entry) => {
            if (!entry || typeof entry !== 'object') return null
            const key =
              typeof (entry as { key?: unknown }).key === 'string' ? (entry as { key: string }).key.trim() : ''
            const value =
              typeof (entry as { value?: unknown }).value === 'string'
                ? (entry as { value: string }).value.trim()
                : ''
            if (!key || !value) return null
            return { key, value } satisfies CatalogMetadataEntry
          })
          .filter((entry): entry is CatalogMetadataEntry => Boolean(entry))
      : []

  const gallery =
    Array.isArray(raw.mediaGallery)
      ? raw.mediaGallery
          .map((item) => (typeof item === 'string' ? item.trim() : ''))
          .filter(Boolean)
      : []

  const mediaVariants =
    raw.mediaVariants && typeof raw.mediaVariants === 'object'
      ? (raw.mediaVariants as CatalogProduct['mediaVariants'])
      : null
  const purchasability = normalizeProductPurchasability(raw.purchasability)
  const stockPolicy = normalizeStockPolicy(raw.stockPolicy) ?? purchasability?.stockPolicy

  return {
    _id: String(raw._id ?? ''),
    name: String(raw.name ?? 'Prodotto'),
    slug: String(raw.slug ?? ''),
    publicCode: normalizePublicCode(raw.publicCode),
    productType: typeof raw.productType === 'string' ? raw.productType.trim().toLowerCase() : undefined,
    categoryId: typeof raw.categoryId === 'string' ? raw.categoryId : undefined,
    categoryIds: Array.isArray(raw.categoryIds)
      ? raw.categoryIds
          .map((value) => (typeof value === 'string' ? value.trim() : ''))
          .filter(Boolean)
      : undefined,
    price: toNumber(raw.price),
    physicalStock: toNumber(raw.physicalStock),
    reservedStock: toNumber(raw.reservedStock),
    availableStock: toNumber(raw.availableStock),
    quantity: toNumber(raw.quantity),
    variants: normalizeProductVariants(raw.variants),
    description: typeof raw.description === 'string' ? raw.description : '',
    mediaVariants,
    mediaGallery: Array.from(
      new Set(
        [...gallery, mediaVariants?.large ?? '', mediaVariants?.medium ?? '', mediaVariants?.small ?? ''].filter(
          Boolean,
        ),
      ),
    ),
    documents: normalizeProductDocuments(raw.documents),
    metadata,
    customFields: normalizeCatalogCustomFields(raw.customFields),
    status:
      raw.status === 'active' || raw.status === 'draft' || raw.status === 'archived'
        ? raw.status
        : undefined,
    stockPolicy,
    isPurchasable:
      typeof raw.isPurchasable === 'boolean' ? raw.isPurchasable : purchasability?.isPurchasable,
    purchasability,
  }
}
